import React, { useState, useEffect, useRef, useCallback  } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/WorkOrderProfile.css';
import EditableCell from '../EditableCell';
import PaymentModal from './PaymentModal';
import { API_URL, apiRequest } from '../../config/api';
import { toast } from 'react-toastify';
import OrderReceipt from '../OrderReceipt';
import OrderWorksheet from '../OrderWorksheet';
import OrderEditModal from './OrderEditModal';
import ClientRejectionModal from './ClientRejectionModal';
import { getCurrentUser } from '../../utils/auth';
import { isIsraelUser } from '../../utils/locationUtils';
import { normalizeOrderType } from '../../utils/orderTypeUtils';
import ProductionSchedule from './ProductionSchedule';
import WigTransferModal from '../WigTransferModal';
import { useWigTransfer } from '../../contexts/WigTransferContext';
import {
  formatDate,
  localDateTimeToUTC,
  formatCurrency
} from '../../utils/dateUtils';
import { parseEventDate, formatAppointmentForDropdown } from '../../utils/appointmentUtils';
import { AppointmentService } from '../../services/appointmentService';
import CreateAppointmentModal  from '../Calendar//CreateAppointmentModal';
import { autoCompleteOrder } from '../../services/autoCompleteService';

import AttachCustomerModal from './AttachCustomerModal';
import AttachMediaModal from './AttachMediaModal';


const WorkOrderProfile = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedOrderNoRef = useRef(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  const [showTransferControls, setShowTransferControls] = useState(false);
  const [transferToRoom, setTransferToRoom] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showWorksheet, setShowWorksheet] = useState(false);
  const [worksheetSpecificData, setWorksheetSpecificData] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showRejectedTasks, setShowRejectedTasks] = useState(false);
  const { openTransferModal } = useWigTransfer();


  const [customerAppointments, setCustomerAppointments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [productionScheduleKey, setProductionScheduleKey] = useState(0);
  const [productionScheduleReady, setProductionScheduleReady] = useState(false);

  const [lineItemsData, setLineItemsData] = useState({
    items: [],
    loaded: false,
    loading: false,
    error: null
  });



  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [newTask, setNewTask] = useState({
    location: '',
    roomNo: '',
    roomTask: '',
    assignedWorker: '',
    taskDueDate: '',
    taskNotes: '',
    qcNotes: '',
    status: 'Not Started'
  });

  const [warrantyInfo, setWarrantyInfo] = useState({
    startDate: null,
    endDate: null,
    status: null,
    daysRemaining: null,
    hasWarranty: false,
    resetCount: 0,
    loaded: false
  });

  // State for warranty end date editing
  const [editingWarrantyEndDate, setEditingWarrantyEndDate] = useState(false);
  const [warrantyEditForm, setWarrantyEditForm] = useState({
    newEndDate: '',
    reason: ''
  });
  const [savingWarrantyEdit, setSavingWarrantyEdit] = useState(false);

  // Media modal
  const [showMediaModal, setShowMediaModal] = useState(false);

  // State for order data
  const [orderData, setOrderData] = useState({
    orderDetails: null,
    customerInfo: null,
    wigDetails: null,
    orderItems: [],
    accessories: [],
    payments: [],
    roomTasks: [],
    totals: { subtotal: 0, tax: 0, taxRate: 0, total: 0 },
    paymentSummary: { invoiceNumber: '', totalCardFees: 0 }
  });


  // Destructure all data from the state
  const {
    orderDetails,
    customerInfo,
    wigDetails,
    orderItems,
    accessories,
    payments,
    roomTasks,
    totals,
    paymentSummary
  } = orderData;

  const availableRooms = orderData?.roomTasks || [];

  // Function to fetch customer details from customer API
  const fetchCustomerDetails = async (customerId) => {
    if (!customerId) return null;

    try {
      const response = await axios.get(`${API_URL}/api/customers/${customerId}`);

      if (response.data) {
        const custData = response.data;
        return {
          customerName: custData.CustomerFullName || `${custData.CustomerFirstName || ''} ${custData.CustomerLastName || ''}`.trim(),
          ID: custData.ID,
          phone: custData.CustomerPhone,
          email: custData.CustomerEmail,
          address: custData.CustomerAddress,
          city: custData.CustomerCity,
          state: custData.CustomerState,
          zip: custData.CustomerZipCode,
          moldId: custData.MoldId,
          notes: custData.CustomerNotes,
          hairlineToCrown: custData.HairlineToCrown,
          hairlineToNape: custData.HairlineToNape,
          earToEar: custData.EarToEar,
          bDiniCare: custData.bDiniCare,
          photoUrl: custData.CustomerPhotoUrl
        };
      }
    } catch (err) {
      console.error('Failed to fetch customer details:', err);
    }
    return null;
  };

  // Function to fetch wig details from inventory by barcode
  const fetchWigDetailsFromInventory = async (barcode) => {
    if (!barcode) return null;

    try {
      const response = await axios.get(`${API_URL}/api/lookup-barcode-enhanced/${barcode}`);

      if (response.data.found && response.data.data) {
        const invData = response.data.data;
        return {
          wigType: invData.Wig_Type,
          netSize: invData.Net_Size,
          netType: invData.Net_Type,
          baseColor: invData.Base_Color,
          hairLength: invData.Wig_Length,
          hairType: invData.Hair_Type,
          laceType: invData.Lace_Type,
          cutStyle: invData.CutByStyle,
          serialCode: invData.Wig_Serial_Code,
          inventoryType: invData.InventoryType,
          invStatus: invData.InvStatus
        };
      }
    } catch (err) {
      console.error('Failed to fetch wig details from inventory:', err);
    }
    return null;
  };

  // Function to fetch order data from the database
  const fetchOrderData = async (orderNo) => {
    if (fetchedOrderNoRef.current === orderNo) return; // prevent duplicate fetches
    fetchedOrderNoRef.current = orderNo;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/orders/order/${orderNo}`);

      if (response.data) {
        const data = response.data;
        const mainItems = [];
        const accessoryItems = [];

        (data.orderItems || []).forEach(item => {
          if (item.itemType === 'ACCESSORY') {
            accessoryItems.push(item);
          } else {
            mainItems.push(item);
          }
        });

        // Fallback customer/wig from order data — render immediately
        const customerFallback = {
          customerName: data.customerName,
          ID: data.customerNo,
          phone: data.customerPhone,
          email: data.customerEmail,
          address: data.customerAddress,
          city: data.customerCity,
          state: data.customerState,
          zip: data.customerZip,
          moldId: data.moldId,
          orderType: data.orderType
        };
        const wigFallback = {
          wigType: data.wigType,
          netSize: data.netSize,
          netType: data.netType,
          baseColor: data.baseColor,
          hairLength: data.wigLength,
          hairType: data.hairType,
          laceType: data.laceType,
          cutStyle: data.cutStyle,
          serialCode: data.wigSerialNo,
          notes: data.orderNotes
        };

        // Set state immediately so the page renders with what we have
        setOrderData({
          orderDetails: {
            ...data,
            orderNo: data.orderNo,
            orderDate: data.orderDate,
            dueDate: data.dueDate,
            orderStatus: data.orderStatus,
            orderType: data.orderType,
            customerName: customerFallback.customerName,
            customerPhone: customerFallback.phone,
            customerEmail: customerFallback.email,
            customerAddress: customerFallback.address,
            customerCity: customerFallback.city,
            customerState: customerFallback.state,
            customerZip: customerFallback.zip,
            wigBarCode: data.wigBarCode,
            wigSerialNo: data.wigSerialNo,
            orderNotes: data.orderNotes,
            customerNo: data.customerNo
          },
          customerInfo: customerFallback,
          wigDetails: wigFallback,
          orderItems: mainItems.length > 0 ? mainItems : data.orderItems || [],
          accessories: accessoryItems,
          payments: data.payments || [],
          roomTasks: data.roomTasks || [],
          totals: data.totals || {
            subtotal: data.subtotal || 0,
            tax: data.tax || 0,
            taxRate: data.taxRate || 0,
            total: data.total || 0
          },
          paymentSummary: {
            invoiceNumber: `INV-${data.orderNo}`,
            totalCardFees: data.paymentSummary?.totalCreditCardFees || 0,
            isComplete: data.orderStatus === 'Completed'
          }
        });
        setIsLoading(false);
        setProductionScheduleReady(true);

        // Fetch accurate customer + wig details in the background, patch in when ready
        Promise.all([
          data.customerNo ? fetchCustomerDetails(data.customerNo) : Promise.resolve(null),
          data.wigBarCode ? fetchWigDetailsFromInventory(data.wigBarCode) : Promise.resolve(null)
        ]).then(([customerDetailsFromAPI, inventoryWigDetails]) => {
          if (!isMountedRef.current) return;
          setOrderData(prev => {
            const customerInfo = customerDetailsFromAPI || prev.customerInfo;
            const wigDetails = inventoryWigDetails || prev.wigDetails;
            return {
              ...prev,
              customerInfo,
              wigDetails,
              orderDetails: {
                ...prev.orderDetails,
                customerName: customerInfo.customerName,
                customerPhone: customerInfo.phone,
                customerEmail: customerInfo.email,
                customerAddress: customerInfo.address,
                customerCity: customerInfo.city,
                customerState: customerInfo.state,
                customerZip: customerInfo.zip,
                wigSerialNo: inventoryWigDetails?.serialCode || prev.orderDetails.wigSerialNo
              }
            };
          });
        });
      }
    } catch (err) {
      setError(`Failed to load order data. Error: ${err.message}`);
      setIsLoading(false);
      setProductionScheduleReady(true);
    }
  };

  const orderNo = params.id || (orderDetails && orderDetails.OrderNo);

  // Initialize the data - either from location state or fetch from API
  useEffect(() => {
    // If we have data in the location state, use it
    if (location.state) {
      setOrderData(location.state);
      setProductionScheduleReady(true);
    }
    // Otherwise, check if we have an order number in the URL
    else if (params.orderNo) {
      fetchOrderData(params.orderNo);
    }
    // If no data in location state and no order number in URL, check the pathname
    else {
      // This allows for manual navigation to /work-order-profile/:orderNo
      const pathParts = location.pathname.split('/');
      const orderNoFromPath = pathParts[pathParts.length - 1];

      if (orderNoFromPath && orderNoFromPath !== 'work-order-profile' && !isNaN(orderNoFromPath)) {
        fetchOrderData(orderNoFromPath);
      } else {
        // If no order number found anywhere, show error
        setError('No order information found. Please return to the orders list.');
      }
    }
  }, [location.pathname, location.state, params.orderNo]);


  // ─────────────────────────────────────────────────────────────────────────

  const fetchWorksheetData = async (orderNo) => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/worksheet/${orderNo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching worksheet data:', error);
      return null;
    }
  };

  const handleEditComplete = async (editResults) => {
    fetchedOrderNoRef.current = null; // reset guard so re-fetch is allowed
    await fetchOrderData(orderDetails?.orderNo);
  };


  const handlePrintWorksheet = useCallback(async () => {
  const worksheetData = await fetchWorksheetData(orderDetails?.orderNo);

  if (worksheetData) {
    setWorksheetSpecificData(worksheetData);
  }
  setShowWorksheet(true);
  document.body.classList.add('modal-open');
}, [orderDetails?.orderNo]); // Make sure fetchWorksheetData is included if it's defined elsewhere


  const handleCloseWorksheet = useCallback(() => {
    setShowWorksheet(false);
    document.body.classList.remove('modal-open');
  }, []);

  // Function to fetch line items from dedicated endpoint
  const fetchLineItemsData = async (orderNumber) => {
    if (!orderNumber || orderNumber === 'New Order') {
      return;
    }

    setLineItemsData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // console.log('🔄 Fetching line items from dedicated endpoint for order:', orderNumber);

      const response = await axios.get(`${API_URL}/api/orders/${orderNumber}/items`);

      if (response.data.success) {
        setLineItemsData({
          items: response.data.items || [],
          loaded: true,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch line items');
      }
    } catch (error) {
      console.error('❌ Error fetching line items:', error);

      setLineItemsData({
        items: [],
        loaded: true,
        loading: false,
        error: error.response?.data?.message || error.message
      });
    }
  };

  const handlePrintReceipt = useCallback(() => {
    if (!orderDetails?.orderNo) {
      console.warn('Cannot print receipt: order data not loaded yet');
      return;
    }

    setShowReceipt(true);
    document.body.classList.add('modal-open');
  }, [orderDetails?.orderNo]);

  const handleCloseReceipt = () => {
    setShowReceipt(false);

    // Remove body class when modal closes
    document.body.classList.remove('modal-open');

  };

  // Combined useEffect - fetch line items and payments in parallel when order loads
  useEffect(() => {
    if (orderDetails?.orderNo) {
      // Fire both fetches in parallel for faster loading
      Promise.all([
        fetchLineItemsData(orderDetails.orderNo),
        fetchPaymentsData(orderDetails.orderNo)
      ]).catch(err => console.error('Error loading order data:', err));
    }
  }, [orderDetails?.orderNo]);


  const handleFetchCustomerAppointments = useCallback(async (customerNo) => {
    try {
      const appointments = await AppointmentService.fetchFilteredAppointments(customerNo);
      setCustomerAppointments(appointments);
      return appointments;
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      setCustomerAppointments([]);
      return [];
    }
  }, []);


  const [showAttachCustomerModal, setShowAttachCustomerModal] = useState(false);






  const [paymentsData, setPaymentsData] = useState({
    payments: [],
    loaded: false,
    loading: false,
    error: null
  });

  // Function to fetch payments from dedicated endpoint
  const fetchPaymentsData = async (orderNumber) => {
    if (!orderNumber || orderNumber === 'New Order') {
      return;
    }

    setPaymentsData(prev => ({ ...prev, loading: true, error: null }));

    try {
      // console.log('🔄 Fetching payments from dedicated endpoint for order:', orderNumber);

      const response = await axios.get(`${API_URL}/api/orders/${orderNumber}/payments`);

      if (response.data.success) {
        setPaymentsData({
          payments: response.data.payments || [],
          loaded: true,
          loading: false,
          error: null
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch payments');
      }
    } catch (error) {
      console.error('❌ Error fetching payments:', error);

      setPaymentsData({
        payments: [],
        loaded: true,
        loading: false,
        error: error.response?.data?.message || error.message
      });
    }
  };

  // Note: fetchPaymentsData is now called in the combined useEffect above (with fetchLineItemsData)

  const handleDeleteLineItem = async (item) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${item.OrderItemDescription}"?`)) {
      return;
    }

    try {
      // 1. Delete from local database first
      const response = await axios.delete(
        `${API_URL}/api/orders/${orderDetails.orderNo}/items/${item.id}`,
        {}
      );

      if (response.data.success) {
        // 2. Refresh line items data to show updated list
        await fetchLineItemsData(orderDetails.orderNo);

        // 3. Show success message
        toast.success('Line item deleted successfully!');

        // 4. Log QB sync status (optional)
        if (response.data.qbSync) {
          if (response.data.qbSync.success) {
            console.log('✅ QB sync successful');
          } else {
            console.warn('⚠️ QB sync failed:', response.data.qbSync.error);
            // Don't show error to user since local delete worked
          }
        }

      } else {
        throw new Error(response.data.message || 'Failed to delete line item');
      }

    } catch (error) {
      console.error('❌ Error deleting line item:', error);
      toast.error(`Failed to delete line item: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      console.log('🗑️ Deleting payment:', paymentId);
      setIsDeleting(paymentId); // Show loading state

      // Find the payment in your payments array
      const paymentToDelete = paymentsData.payments.find(p => p.id === paymentId);
        if (!paymentToDelete) {
          alert('Payment not found');
          return;
        }
      // Get the order number from orderDetails.orderNo (your actual structure)
      const orderNumber = orderDetails?.orderNo;

      if (!orderNumber) {
        console.error('Order number not found');
        alert('Order number not found');
        return;
      }

      const response = await axios.delete(`https://diniwigtracker-api.super-bread-aa95.workers.dev/api/payments/delete/${paymentId}`, {
        data: {
          orderNo: orderNumber,
          paymentAmount: paymentToDelete.amount,
          paymentMethod: paymentToDelete.method,
          paymentNotes: paymentToDelete.notes || '',
          qbPaymentId: paymentToDelete.qbPaymentId,
          qbSyncToken: paymentToDelete.qbSyncToken
        },
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update your payments state by updating the orderData
      setOrderData(prevData => ({
        ...prevData,
        payments: prevData.payments.filter(p => p.id !== paymentId)
      }));

      // Show success message
      alert('Payment deleted successfully!');

    } catch (error) {
      console.error('❌ Error deleting payment:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show error message to user
      alert(`Failed to delete payment: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsDeleting(null); // Clear loading state
    }
  };



  const handleClientRejectOrder = () => {
    setShowRejectionModal(true);
  };

  // 4. Add the rejection completion handler:
  const handleRejectionComplete = async (rejectionData) => {
    // Refresh the order data to show updated tasks
    if (orderDetails?.orderNo) {
      try {
        console.log('🔄 Refreshing order data after rejection...');
        await fetchOrderData(orderDetails.orderNo);

        // Optionally show a success message
        toast.success('Client rejection process completed successfully!', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('❌ Error refreshing order data:', error);
      }
    }
  };






  // Initialize with proper structure to prevent undefined errors
  // const [roomsData, setRoomsData] = useState({
  //   Pomona: { rooms: [], tasks: {}, workers: {} },
  //   Lakewood: { rooms: [], tasks: {}, workers: {} },
  //   Israel: { rooms: [], tasks: {}, workers: {} },
  //   China: { rooms: [], tasks: {}, workers: {} }
  // });


  // SAFE useEffect for fetching rooms data
  // useEffect(() => {
  //   const fetchRoomsForLocation = async (location) => {
  //     try {
  //       const response = await axios.get(`${API_URL}/api/get-rooms/${location}`);
  //       return response.data.map(room => ({
  //         roomNo: room.RoomNo,
  //         roomName: `${room.RoomNo} ${room.RoomDescription}`,
  //         parentRoomNo: room.ParentRoomNo
  //       }));
  //     } catch (error) {
  //       console.error(`Error fetching ${location} rooms:`, error);
  //       return []; // Return empty array on error
  //     }
  //   };

  //   // const fetchAllRooms = async () => {
  //   //   const locations = ['Pomona', 'Lakewood', 'Israel', 'China'];
  //   //   const results = await Promise.all(locations.map(loc => fetchRoomsForLocation(loc)));
  //   //   const newRoomsData = {
  //   //     Pomona: { rooms: results[0], tasks: {}, workers: {} },
  //   //     Lakewood: { rooms: results[1], tasks: {}, workers: {} },
  //   //     Israel: { rooms: results[2], tasks: {}, workers: {} },
  //   //     China: { rooms: results[3], tasks: {}, workers: {} }
  //   //   };
  //   //   setRoomsData(newRoomsData);
  //   // };

  //   // fetchAllRooms();
  // }, []);




  const calculateTaxInfoForPayment = () => {
    const isTaxable = shouldApplyTax(orderDetails, customerInfo);
    const orderType = orderDetails?.orderType || customerInfo?.orderType;

    if (!isTaxable) {
      return {
        isTaxable: false,
        taxRate: 0,
        originalTaxAmount: 0,
        paidTaxAmount: 0,
        remainingTaxBalance: 0,
        orderType: orderType
      };
    }


    // Find tax line item in order items
    const allItems = [...(orderItems || []), ...(accessories || [])];
    const taxLineItem = allItems.find(item =>
      item.description && (
        item.description.toLowerCase().includes('state tax applied') ||
        item.description.toLowerCase().includes('tax applied') ||
        item.description.toLowerCase().includes('sales tax') ||
        item.description.toLowerCase().includes('tax:') ||
        item.description.includes('%')
      )
    );

    let taxRate = 0;
    let originalTaxAmount = 0;

    if (taxLineItem) {

      // Extract tax rate from description
      const taxRateMatch = taxLineItem.description.match(/(\d+\.?\d*)%/);
      if (taxRateMatch) {
        taxRate = parseFloat(taxRateMatch[1]);
      }

      originalTaxAmount = parseFloat(taxLineItem.price || taxLineItem.extendedCost || 0);
    } else {

      // Fallback to totals object
      if (totals?.taxRate > 0) {
        taxRate = totals.taxRate;
        originalTaxAmount = totals.tax || 0;
      }
    }

    // Calculate how much tax has been paid (look for tax-specific payments)
    const taxPayments = (payments || []).filter(payment =>
      payment.notes && payment.notes.toLowerCase().includes('tax')
    );

    const paidTaxAmount = taxPayments.reduce((sum, payment) =>
      sum + parseFloat(payment.amount || 0), 0
    );

    // Calculate remaining tax balance
    const remainingTaxBalance = Math.max(0, originalTaxAmount - paidTaxAmount);

    const result = {
      isTaxable: true,
      taxRate,
      originalTaxAmount,
      paidTaxAmount,
      remainingTaxBalance,
      orderType
    };

    return result;
  };


  // Updated shouldApplyTax function
  const shouldApplyTax = (orderDetails, customerInfo) => {
    const orderType = orderDetails?.orderType || customerInfo?.orderType;
    const taxableTypes = ['New Wig', 'New Sale Wig'];
    return taxableTypes.some(type =>
      orderType?.toLowerCase().includes(type.toLowerCase())
    );
  };


  // Handle going back
  const handleGoBack = () => {
    navigate('/orderdb');
  };



  const [warrantyDataRequested, setWarrantyDataRequested] = useState(false);


  const fetchWarrantyInfo = async (orderNumber) => {
    try {
      console.log('Fetching warranty info for order:', orderNumber);

      const response = await axios.get(`${API_URL}/api/orders/${orderNumber}/warranty`);

      if (response.data.success && response.data.warranty) {
        const warranty = response.data.warranty;

        console.log('Raw warranty data from API:', warranty);

        // Support both snake_case (from DB) and camelCase naming
        const startDate = warranty.startDate || warranty.Wig_Warranty_Start_Date;
        const endDate = warranty.endDate || warranty.Wig_Warranty_Expires_Date;
        const wigBarCode = warranty.wigBarCode || warranty.Wig_Bar_Code;
        const resetCount = warranty.resetCount ?? warranty.WarrantyResetCount ?? 0;

        // Check if we have valid warranty data with dates
        if (endDate && startDate) {
          // Calculate days remaining
          const today = new Date();
          const endDateObj = new Date(endDate);
          const daysRemaining = Math.max(0, Math.ceil((endDateObj - today) / (1000 * 60 * 60 * 24)));

          // Check if warranty is actually active (not expired)
          const isActive = daysRemaining > 0;

          setWarrantyInfo({
            startDate: startDate,
            endDate: endDate,
            status: isActive ? (warranty.status || warranty.Wig_Warranty_Status || 'Active') : 'Expired',
            daysRemaining,
            hasWarranty: true,
            isActive: isActive,
            resetCount: resetCount,
            loaded: true,
            wigBarCode: wigBarCode
          });

          console.log(`✅ Warranty loaded - ${isActive ? 'Active' : 'Expired'} (Days remaining: ${daysRemaining})`);
        } else {
          // No warranty data found via API
          setWarrantyInfo({
            startDate: null,
            endDate: null,
            status: null,
            daysRemaining: null,
            hasWarranty: false,
            isActive: false,
            resetCount: 0,
            loaded: true,
            message: response.data.message || 'No warranty found'
          });

          console.log('ℹ️ No warranty found via API');
        }
      } else {
        // API returned no warranty data
        setWarrantyInfo({
          startDate: null,
          endDate: null,
          status: null,
          daysRemaining: null,
          hasWarranty: false,
          isActive: false,
          resetCount: 0,
          loaded: true,
          message: response.data.message || 'No warranty data available'
        });
      }
    } catch (error) {
      console.error('❌ Error fetching warranty info:', error);

      // On error, set no warranty state
      setWarrantyInfo({
        startDate: null,
        endDate: null,
        status: null,
        daysRemaining: null,
        hasWarranty: false,
        isActive: false,
        resetCount: 0,
        loaded: true,
        error: true
      });
    }
  };

    // Check if reset button should be enabled
  const canResetWarranty = () => {
    // Must have warranty
    if (!warrantyInfo.hasWarranty) return false;

    // Can only reset once
    if (warrantyInfo.resetCount >= 1) return false;

    // Only New Wig orders can reset warranty ("Aidel New Wig" included)
    if (normalizeOrderType(orderDetails?.orderType) !== 'New Wig') return false;

    // Balance must be paid off
    const totalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const allItems = [...(orderItems || []), ...(accessories || [])];
    const subtotal = allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
    const balanceDue = subtotal - totalPaid;

    return balanceDue <= 0.01; // Allow for small rounding differences
};

    // Add this function to reset warranty
  const handleResetWarranty = async () => {
    if (!orderDetails?.orderNo) {
      toast.error('No order number found');
      return;
    }

    // Check if balance is paid off
    const totalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const allItems = [...(orderItems || []), ...(accessories || [])];
    const subtotal = allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
    const balanceDue = subtotal - totalPaid;

    if (balanceDue > 0.01) { // Allow for small rounding differences
      toast.error(`Cannot reset warranty. Balance due: ${formatCurrency(balanceDue)}`);
      return;
    }

    // Check if warranty has already been reset
    if (warrantyInfo.resetCount >= 1) {
      toast.error('Warranty has already been reset once. Cannot reset again.');
      return;
    }

    // Confirm with user using toast confirmation
    const confirmReset = () => {
      return new Promise((resolve) => {
        toast(
          (t) => (
            <div className="flex flex-col space-y-3">
              <div className="font-medium text-gray-900">
                Reset Warranty?
              </div>
              <div className="text-sm text-gray-600">
                Are you sure you want to reset the warranty? This can only be done once and will start a new 9 month warranty period.
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    resolve(true);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm font-medium"
                >
                  Yes, Reset
                </button>
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    resolve(false);
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1.5 rounded text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          ),
          {
            duration: 0, // Don't auto-dismiss
            style: {
              maxWidth: '400px',
            }
          }
        );
      });
    };

    // Show confirmation toast
    const confirmed = await confirmReset();
    if (!confirmed) {
      return;
    }

    try {
      console.log('Resetting warranty for order:', orderDetails.orderNo);

      // Show loading toast
      const loadingToastId = toast.loading('Resetting warranty...');

      const response = await axios.post(`${API_URL}/api/orders/${orderDetails.orderNo}/reset-warranty`);

      // Dismiss loading toast
      toast.dismiss(loadingToastId);

      if (response.data.success) {
        // Refresh warranty info
        await fetchWarrantyInfo(orderDetails.orderNo);
        toast.success('Warranty reset successfully! New 9 month warranty period has started.', {
          duration: 5000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to reset warranty');
      }
    } catch (error) {
      console.error('❌ Error resetting warranty:', error);
      toast.error(`Failed to reset warranty: ${error.response?.data?.message || error.message}`, {
        duration: 6000,
      });
    }
  };

  // Handler to open warranty end date edit modal
  const handleOpenWarrantyEdit = () => {
    // Check if balance is paid off
    const totalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const allItems = [...(orderItems || []), ...(accessories || [])];
    const subtotal = allItems.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
    const balanceDue = subtotal - totalPaid;

    if (balanceDue > 0.01) {
      toast.error(`Cannot edit warranty. Balance must be paid in full. Balance due: ${formatCurrency(balanceDue)}`);
      return;
    }

    // Check if warranty has already been reset
    if (warrantyInfo.resetCount >= 1) {
      toast.error('Warranty has already been manually edited once. Cannot edit again.');
      return;
    }

    // Initialize form with current end date
    setWarrantyEditForm({
      newEndDate: warrantyInfo.endDate ? new Date(warrantyInfo.endDate).toISOString().split('T')[0] : '',
      reason: ''
    });
    setEditingWarrantyEndDate(true);
  };

  // Handler to save warranty end date edit
  const handleSaveWarrantyEdit = async () => {
    if (!warrantyEditForm.newEndDate) {
      toast.error('Please select a new end date');
      return;
    }

    if (!warrantyEditForm.reason || warrantyEditForm.reason.trim().length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters) for this manual warranty update');
      return;
    }

    setSavingWarrantyEdit(true);

    try {
      const response = await axios.put(`${API_URL}/api/orders/${orderDetails.orderNo}/warranty/end-date`, {
        newEndDate: warrantyEditForm.newEndDate,
        reason: warrantyEditForm.reason.trim(),
        updatedBy: currentUser
      });

      if (response.data.success) {
        toast.success('Warranty end date updated successfully');

        // Refresh warranty info
        await fetchWarrantyInfo(orderDetails.orderNo);

        // Close modal
        setEditingWarrantyEndDate(false);
        setWarrantyEditForm({ newEndDate: '', reason: '' });
      } else {
        toast.error(response.data.message || 'Failed to update warranty end date');
      }
    } catch (error) {
      console.error('Error updating warranty end date:', error);
      toast.error(error.response?.data?.message || 'Failed to update warranty end date');
    } finally {
      setSavingWarrantyEdit(false);
    }
  };

  const handleSaveBarcode = async (newBarcode) => {
    await axios.put(`${API_URL}/api/orders/${orderDetails.orderNo}/wig-codes`, {
      wigBarCode: newBarcode,
      wigSerialCode: wigDetails?.serialCode || orderDetails?.wigSerialNo || ''
    });
    const freshWigDetails = await fetchWigDetailsFromInventory(newBarcode);
    setOrderData(prev => ({
      ...prev,
      orderDetails: {
        ...prev.orderDetails,
        wigBarCode: newBarcode,
        wigSerialNo: freshWigDetails?.serialCode || prev.orderDetails.wigSerialNo
      },
      wigDetails: freshWigDetails || prev.wigDetails
    }));
    toast.success('Barcode updated!');
  };

  const handleSaveSerialCode = async (newSerial) => {
    await axios.put(`${API_URL}/api/orders/${orderDetails.orderNo}/wig-codes`, {
      wigBarCode: orderDetails?.wigBarCode || '',
      wigSerialCode: newSerial
    });
    setOrderData(prev => ({
      ...prev,
      orderDetails: { ...prev.orderDetails, wigSerialNo: newSerial },
      wigDetails: { ...prev.wigDetails, serialCode: newSerial }
    }));
    toast.success('Serial code updated!');
  };

  const loadWarrantyDataOnDemand = useCallback(async () => {
    if (warrantyDataRequested || warrantyInfo.loaded) {
      return; // Already loaded or loading
    }

    setWarrantyDataRequested(true);
    const orderNumber = orderDetails?.orderNo || orderNo;

    if (orderNumber && orderNumber !== 'New Order') {
      console.log('🔄 Loading warranty data on demand for order:', orderNumber);
      await fetchWarrantyInfo(orderNumber);
    }
  }, [warrantyDataRequested, warrantyInfo.loaded, orderDetails?.orderNo, orderNo]);

  const getWarrantyStatusClass = (status, daysRemaining) => {
    if (status === 'Expired') return 'text-danger';
    if (daysRemaining <= 30) return 'text-warning';
    if (daysRemaining <= 90) return 'text-info';
    return 'text-success';
  };

  // Helper function to format warranty status
  const getWarrantyStatusText = (status, daysRemaining) => {
    if (status === 'Expired') return 'Expired';
    if (daysRemaining === 0) return 'Expires Today';
    if (daysRemaining === 1) return '1 Day Remaining';
    if (daysRemaining <= 30) return `${daysRemaining} Days Remaining (Expiring Soon)`;
    if (daysRemaining <= 90) return `${daysRemaining} Days Remaining`;
    return `${daysRemaining} Days Remaining`;
  };


  // QB data is loaded on-demand when user clicks "Load Invoice Info" button
  // Removed automatic loading to improve initial page load time

  useEffect(() => {
  if (location.state?.triggerAction) {
    const action = location.state.triggerAction;

    // Clear the trigger action from state to prevent re-triggering on refresh
    navigate(location.pathname, { replace: true });

    if (action === 'printWorksheet') {
      // Small delay to ensure component is fully loaded and data is available
      setTimeout(() => {
        if (orderDetails?.orderNo) {
          handlePrintWorksheet();
        }
      }, 500); // Give time for order data to load
    } else if (action === 'printReceipt') {
      setTimeout(() => {
        if (orderDetails?.orderNo) {
          handlePrintReceipt();
        }
      }, 500);
    }
  }
}, [location.state, navigate, orderDetails?.orderNo]); // Dependencies

  const handleDueDateUpdate = async (newDate, newAppointmentId = null) => {
  try {
    // Convert local time to UTC for database storage
    let utcDateTime;

    if (newDate.includes('T')) {
      // Already has time, convert to UTC
      utcDateTime = localDateTimeToUTC(newDate);
    } else {
      // Just a date, add noon time and convert to UTC
      utcDateTime = localDateTimeToUTC(newDate + 'T12:00:00');
    }

    // console.log('🔍 Original date:', newDate);
    // console.log('🔍 UTC datetime being sent:', utcDateTime);
    // console.log('🔍 New appointment ID:', newAppointmentId);

    const response = await axios.put(`${API_URL}/api/orders/${orderDetails.orderNo}/due-date`, {
      dueDate: utcDateTime,
      newAppointmentId: newAppointmentId
    });
    if (response.data.success) {
      // Update local state with the formatted date
      setOrderData(prev => ({
        ...prev,
        orderDetails: {
          ...prev.orderDetails,
          dueDate: utcDateTime
        },
        roomTasks: prev.roomTasks.map(task =>
          task.roomTask === 'Customer Pickup'
            ? { ...task, taskDueDate: utcDateTime }
            : task
        )
      }));
      setProductionScheduleKey(prev => prev + 1);
      toast.success('Due date updated successfully!');
    }
  } catch (error) {
    console.error('❌ Full error details:', error.response?.data);
    toast.error(`Failed to update due date: ${error.response?.data?.message || error.message}`);
  }
};

  const [showAutoCompletePrompt, setShowAutoCompletePrompt] = useState(false);
  const [isAutoCompleting, setIsAutoCompleting] = useState(false);

  const handlePaymentSuccess = async (paymentResult) => {
    console.log('Payment processed successfully:', paymentResult);
    alert(`Payment of $${paymentResult.payment?.amount} processed successfully!`);

    if (!orderDetails?.orderNo) return;

    // Refresh the payments UI
    fetchPaymentsData(orderDetails.orderNo);

    // Check if the order is now fully paid
    try {
      const [itemsRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/api/orders/${orderDetails.orderNo}/items`),
        fetch(`${API_URL}/api/orders/${orderDetails.orderNo}/payments`)
      ]);
      const [itemsData, paymentsData] = await Promise.all([itemsRes.json(), paymentsRes.json()]);

      const orderTotal = (itemsData.items || []).reduce(
        (sum, item) => sum + parseFloat(item.OrderItemExtendedCost || 0), 0
      );
      const totalPaid = (paymentsData.payments || []).reduce(
        (sum, p) => sum + parseFloat(p.PaymentAmount || 0), 0
      );
      const balanceDue = orderTotal - totalPaid;

      // Show prompt only if fully paid and not already completed
      if (balanceDue <= 0.01 && orderDetails.orderStatus !== 'Completed') {
        setShowAutoCompletePrompt(true);
      }
    } catch (err) {
      console.error('Balance check after payment failed:', err);
    }
  };

  const handleAutoCompleteFromPrompt = async () => {
    setIsAutoCompleting(true);
    try {
      const currentUser = getCurrentUser();
      await autoCompleteOrder(orderDetails.orderNo, currentUser);
      toast.success(`Order ${orderDetails.orderNo} auto-completed successfully!`);
      setShowAutoCompletePrompt(false);
    } catch (err) {
      toast.error(`Auto-complete failed: ${err.message}`);
    } finally {
      setIsAutoCompleting(false);
    }
  };






  // Get order status class
  const getStatusClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending order':
        return 'status-warning';
      case 'pending':
        return 'status-warning';
      case 'in progress':
        return 'status-info';
      case 'in process':
        return 'status-info';
      case 'completed':
        return 'status-success';
      case 'cancelled':
        return 'status-error';
      case 'delayed':
        return 'status-error';
      case 'active':
        return 'status-info';
      case 'task completed':
        return 'status-success';
      default:
        return 'status-default';
    }
  };

  if (isLoading) {
    return (
      <div className="container p-3 text-center">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p>Loading order data...</p>
      </div>
    );
  }

  // Add defensive checks for rendering
  if (!orderDetails) {
    return (
      <div className="container p-3 text-center">
        <h5>No order data found</h5>
        <button
          className="btn btn-primary mt-2"
          onClick={() => navigate('/orderdb')}
        >
          <i className="fas fa-arrow-left mr-1"></i> Return to Orders List
        </button>
      </div>
    );
  }



const updatePaymentNotes = async (journalId, paymentNotes) => {
  const response = await fetch(`${API_URL}/api/rachels-room/journal/${journalId}/payment-notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentNotes }),
  });
  if (!response.ok) throw new Error('Failed to update payment notes');
  setPaymentsData(prev => ({
    ...prev,
    payments: prev.payments.map(p => p.id === journalId ? { ...p, PaymentNotes: paymentNotes } : p)
  }));
};


// Updated renderInvoiceCalculation function with delete functionality
  const renderInvoiceCalculation = () => {
    // Use the dedicated line items data
    const currentItems = lineItemsData.items;

    // Simple total calculation - just sum what's actually in the database
    const orderTotal = currentItems.reduce((sum, item) => {
      const itemTotal = parseFloat(item.OrderItemExtendedCost || 0);
      return sum + itemTotal;
    }, 0);


    return (
      <>
        {/* Loading state for line items */}
        {lineItemsData.loading && (
          <div className="text-center py-4 mb-4">
            <div className="spinner-border spinner-border-sm mr-2" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            Loading line items...
          </div>
        )}

        {/* Error state for line items */}
        {lineItemsData.error && (
          <div className="alert alert-warning mb-4">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            Error loading line items: {lineItemsData.error}
            <button
              className="btn btn-sm btn-outline-secondary ml-2"
              onClick={() => fetchLineItemsData(orderDetails?.orderNo)}
            >
              Retry
            </button>
          </div>
        )}

        {/* Line Items Table - Only show when loaded */}
        {lineItemsData.loaded && !lineItemsData.loading && (
          <div className="table-responsive-custom mb-4">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <h6 className="card-title mb-0">Line Items
                 <button
                className="refresh"
                onClick={() => fetchLineItemsData(orderDetails?.orderNo)}
                disabled={lineItemsData.loading}
                title="Refresh line items"
              >
                <i className={`fas fa-sync-alt ${lineItemsData.loading ? 'fa-spin' : ''}`}></i>
              </button>
              </h6>

            </div>

            <table className="table">
              <thead className="bg-light">
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr key={item.id || index}>
                    <td data-label="Description">
                      {item.OrderItemDescription}
                      {item.OrderItemComments && (
                        <small className="text-muted d-block">
                          <i className="fas fa-comment mr-1"></i>
                          {item.OrderItemComments}
                        </small>
                      )}
                    </td>
                    <td data-label="Qty" className="text-md-right">
                      {item.OrderQty}
                    </td>
                    <td data-label="Unit Price" className="text-md-right">
                      {formatCurrency(item.OrderItemRetailCost)}
                    </td>
                    <td data-label="Total" className="text-md-right">
                      {formatCurrency(item.OrderItemExtendedCost)}
                    </td>
                    <td data-label="Actions" className="text-center">
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDeleteLineItem(item)}
                        title="Delete line item"
                      >
                        <i className="fas fa-trash"></i>
                        <span className="d-none d-md-inline ml-1"></span>
                      </button>
                    </td>
                  </tr>
                ))}

                {/* Show message if no items */}
                {currentItems.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      <i className="fas fa-info-circle mr-2"></i>
                      No line items found for this order
                    </td>
                  </tr>
                )}

                {/* Simple total row */}
                {currentItems.length > 0 && (
                  <tr className="summary-row bg-light">
                    <td colSpan="3" className="d-none d-md-table-cell"></td>
                    <td data-label="Total" className="text-md-right">
                      <strong>Total</strong>
                    </td>
                    <td data-label="" className="text-md-right">
                      <strong>{formatCurrency(orderTotal)}</strong>
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Use our clean payment components */}
        {renderPaymentSummary()}
        {renderBalanceSummary()}
      </>
    );
  };
  const renderPaymentSummary = () => {
    // Use the dedicated payments data instead of the processed payments
    const currentPayments = paymentsData.payments;

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <i className="fas fa-credit-card mr-2"></i>
            <span className="card-title mb-0">
            Payment Summary
            </span>
              <button
            className="refresh"
            onClick={() => fetchPaymentsData(orderDetails?.orderNo)}
            disabled={paymentsData.loading}
            title="Refresh payment data"
          >
            <i className={`fas fa-sync-alt ${paymentsData.loading ? 'fa-spin' : ''}`}></i>
          </button>
          </h6>

        </div>

        {paymentsData.loading && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm mr-2" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            Loading payments...
          </div>
        )}

        {paymentsData.error && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            Error loading payments: {paymentsData.error}
          </div>
        )}

        {paymentsData.loaded && !paymentsData.loading && (
          <>
            {currentPayments.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover" style={{ fontSize: '1rem' }}>
                  <thead>
                    <tr className="bg-light">
                      <th>Date</th>
                      <th>Method</th>
                      <th>Description</th>
                      <th>Notes</th>
                      <th>User</th>
                      <th>Transaction ID</th>
                      <th className="text-right">Amount</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPayments.map((payment, index) => {
                      const paymentDate = payment.PaymentDate || payment.DateAdded;
                      const formattedDate = paymentDate ? formatDate(paymentDate) : 'N/A';
                      const ccFee = parseFloat(payment.CreditCardFee || 0);
                      const paymentMethod = payment.PaymentMethod || 'Unknown';
                      const paymentDescription = payment.PaymentType || 'Payment';
                      const paymentNotes = payment.PaymentNotes || '';
                      const paymentAmount = parseFloat(payment.PaymentAmount || 0);

                      const methodIcon = paymentMethod?.toLowerCase().includes('card') ? 'credit-card' :
                        paymentMethod?.toLowerCase().includes('cash') ? 'money-bill-alt' :
                        paymentMethod?.toLowerCase().includes('check') ? 'money-check' :
                        'money-bill-wave';

                      return (
                        <tr key={payment.id || index}>
                          <td className="align-middle">{formattedDate}</td>
                          <td className="align-middle">
                            <i className={`fas fa-${methodIcon} text-secondary`}></i>
                            {paymentMethod}
                            {ccFee > 0 && (
                              <span >+{formatCurrency(ccFee)} fee</span>
                            )}
                          </td>
                          <td className="align-middle">{paymentDescription}</td>
                          <td className="align-middle">
                            <EditableCell
                              value = {paymentNotes}
                              onSave={(value) => updatePaymentNotes(payment.id, value)}
                              multiline={true}
                              placeholder='Add Note..'

                            />
                            </td>
                          <td className="align-middle text-muted">
                            {payment.PaymentAcceptedBy ? (
                              <><i className="fas fa-user mr-1"></i>{payment.PaymentAcceptedBy}</>
                            ) : '—'}
                          </td>
                          <td className="align-middle text-muted">
                            {payment.vchCloverTransactionId ? (
                              <><i className="fas fa-receipt mr-1"></i>{payment.vchCloverTransactionId}</>
                            ) : '—'}
                          </td>
                          <td className="align-middle text-right">
                            <strong className="text-success">{formatCurrency(paymentAmount)}</strong>
                          </td>
                          <td className="align-middle text-center">
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              disabled={isDeleting === payment.id}
                              // className="btn btn-sm btn-link text-danger p-0"
                              title="Delete payment"
                            >
                              {isDeleting === payment.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-trash-alt"></i>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4 bg-light rounded">
                <i className="fas fa-info-circle mr-2 text-muted"></i>
                <span className="text-muted">No payments recorded</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  // Updated balance summary to use line items data
  const renderBalanceSummary = () => {
    // Calculate total from dedicated line items endpoint
    const orderTotal = lineItemsData.items.reduce((sum, item) => {
      return sum + parseFloat(item.OrderItemExtendedCost || 0);
    }, 0);

    // Calculate total payments using the dedicated payments data
    const totalPaid = paymentsData.payments.reduce((sum, payment) => {
      return sum + parseFloat(payment.PaymentAmount || 0);
    }, 0);

    // Calculate balance due
    const balanceDue = orderTotal - totalPaid;

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', marginRight: '1rem' }}>
        <div className="bg-light rounded p-3" style={{ minWidth: '320px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', marginBottom: '0.25rem' }}>
            <span className="text-muted">Order Total:</span>
            <span>{formatCurrency(orderTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', marginBottom: '0.5rem' }}>
            <span className="text-muted">Amount Paid:</span>
            <span>{formatCurrency(totalPaid)}</span>
          </div>
          <hr className="my-2" />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', alignItems: 'center' }}>
            <strong>Balance Due:</strong>
            <span className={balanceDue > 0 ? 'text-danger' : 'text-success'}>
              <strong style={{ fontSize: '1.1rem' }}>{formatCurrency(Math.abs(balanceDue))}</strong>
              {balanceDue < 0 && <small className="text-muted ml-1">(Credit)</small>}
            </span>
          </div>
        </div>
      </div>
    );
  };

return (
  <div className="container p-3">

      {/* Customer Info Section with two-column layout */}
      <div className="card mb-4 shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-md-row flex-column">
            <div className="order-header-info">
              <div className="order-title-section mb-3">
                <h3>
                  <i className="fas fa-receipt text-primary mr-2"></i>
                  Order #{orderDetails.orderNo}
                </h3>

                <div
                  className="order-type-status-row d-flex justify-content-between align-items-center mb-3"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    width: '100%'
                  }}
                >
                  <div
                    className="order-type-badge"
                    style={{
                      marginRight: 'auto',
                      flexShrink: 0
                    }}
                  >
                    <i className="fas fa-tag mr-2"></i>
                    <span className="order-type-text">{orderDetails.orderType}</span>
                  </div>

                  <span
                    className={`status-badge ${getStatusClass(orderDetails.orderStatus)}`}
                  >
                    <i className="fas fa-circle mr-2" style={{ fontSize: '0.6rem' }}></i>
                    {orderDetails.orderStatus || 'Pending'}
                  </span>

                </div>
                 <div className="order-dates-row">
                <div className="date-item" style={{ marginRight: 'auto' }}>
                  <i className="far fa-calendar-plus text-primary mr-2"></i>
                  <span className="date-label">Created:</span>
                 <span className="date-value ml-1">{orderDetails.orderDate ? formatDate(orderDetails.orderDate) : 'N/A'}</span>
                 {orderDetails.enteredBy && (
                   <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                     by <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>{orderDetails.enteredBy}</span>
                   </span>
                 )}
                </div>
                {orderDetails.dueDate && (
                  <div className="date-item" style={{ marginLeft: 'auto' }}>
                    <i className="far fa-calendar-check text-success mr-2"></i>
                    <select
                      value={(() => {
                        // First check if current due date matches any appointment
                        const matchingAppointment = customerAppointments.find(apt => {
                          const aptDate = parseEventDate(apt.EventStart);
                          const dueDate = new Date(orderDetails.dueDate);
                          return aptDate && dueDate &&
                                aptDate.toDateString() === dueDate.toDateString();
                        });

                        if (matchingAppointment) {
                          return matchingAppointment.EventStart;
                        }

                        // If no matching appointment but we have a due date, return the due date
                        if (orderDetails.dueDate) {
                          return orderDetails.dueDate;
                        }

                        // Otherwise empty
                        return '';
                      })()}
                        onFocus={async () => {
                          const customerId = customerInfo?.ID || customerInfo?.customerNo || orderDetails?.customerNo;


                          if (!customerId) {
                            console.warn('Cannot refresh appointments: customer ID is undefined');
                            return;
                          }

                          try {
                            await handleFetchCustomerAppointments(customerId);
                          } catch (error) {
                            console.error('Failed to refresh appointments:', error);
                          }
                        }}
                      onChange={(e) => {
                        if (e.target.value === 'CREATE_NEW') {
                          setShowModal(true);
                        } else if (e.target.value) {
                          // Find the selected appointment by EventStart to get its ID
                          const selectedAppointment = customerAppointments.find(
                            apt => apt.EventStart === e.target.value
                          );
                          const appointmentId = selectedAppointment?.ID || selectedAppointment?.id;

                          console.log('🔍 Selected appointment:', selectedAppointment);
                          console.log('🔍 Appointment ID:', appointmentId);

                          // Pass both date and appointment ID
                          handleDueDateUpdate(e.target.value, appointmentId);
                        }
                      }}
                      className="form-control form-control-sm date-value ml-1"
                      style={{
                        display: 'inline-block',
                        width: 'auto',
                        minWidth: '150px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="">Select appointment...</option>

                      {/* Show current due date if it doesn't match any appointment */}
                      {orderDetails.dueDate && !customerAppointments.find(apt => {
                        const aptDate = parseEventDate(apt.EventStart);
                        const dueDate = new Date(orderDetails.dueDate);
                        return aptDate && dueDate && aptDate.toDateString() === dueDate.toDateString();
                      }) && (
                        <option value={orderDetails.dueDate} style={{
                          fontStyle: 'italic',
                          color: '#666',
                          backgroundColor: '#f5f5f5'
                        }}>
                          Due: {formatDate(orderDetails.dueDate)} (No appointment)
                        </option>
                      )}

                      <option value="CREATE_NEW" style={{
                        fontWeight: 'bold',
                        color: '#4027b0',
                        backgroundColor: '#f8f9fa'
                      }}>
                        + Create appointment
                      </option>

                      {Array.isArray(customerAppointments) &&
                        customerAppointments
                          .filter(appointment => {
                            if (!appointment.EventStart) return false;

                            const appointmentDate = parseEventDate(appointment.EventStart);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            return appointmentDate && appointmentDate >= today;
                          })
                          .map((appointment, index) => {
                            const eventDate = parseEventDate(appointment.EventStart);
                            const formattedDate = eventDate ? eventDate.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Invalid Date';

                            return (
                              <option key={index} value={appointment.EventStart}>
                                {formattedDate} - {appointment.EventDesc || 'Appointment'}
                              </option>
                            );
                          })
                      }
                    </select>
                  </div>
                )}
              </div>
              </div>


              {/* Customer Info Integrated - Using Your CSS Classes */}
              <div className="customer-info-condensed">
                <div >


                  {/* Customer Details Grid */}
                  <div className="customer-details-3row" style={{ flex: 1 }}>
                    {/* Row 1: Name | Email | Address (Street) */}
                    <div className="customer-row-wop">
                      <div className="customer-detail-item">
                        <strong>Name:</strong>{' '}
                        <Link
                          to={`/customers/${customerInfo?.ID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {customerInfo?.customerName || 'N/A'}
                        </Link>
                        {customerInfo?.bDiniCare && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            backgroundColor: '#4027b0',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            DiNi Care
                          </span>
                        )}
                      </div>
                      <div className="customer-detail-item">
                        <strong>Email:</strong> {customerInfo?.email || 'N/A'}
                      </div>
                      <div className="customer-detail-item">
                        <strong>Address:</strong> {customerInfo?.address || 'N/A'}
                      </div>
                    </div>

                    {/* Row 2: Phone | Mold ID | City/State */}
                    <div className="customer-row-wop">
                      <div className="customer-detail-item">
                        <strong>Phone:</strong> {customerInfo?.phone || 'N/A'}
                      </div>
                      <div className="customer-detail-item">
                        <strong>Mold ID:</strong> {customerInfo?.moldId || 'N/A'}
                      </div>
                      <div className="customer-detail-item">
                        <strong>City/State:</strong> {
                          [
                            customerInfo?.city,
                            customerInfo?.state,
                            customerInfo?.zip
                          ].filter(Boolean).join(', ') || 'N/A'
                        }
                      </div>

                    </div>

                    {/* Row 3: Measurements */}
                    {(customerInfo?.hairlineToCrown || customerInfo?.hairlineToNape || customerInfo?.earToEar) && (
                      <div className="customer-row-wop">
                        <div className="customer-detail-item">
                          <strong>Hairline to Crown:</strong> {customerInfo?.hairlineToCrown || 'N/A'}
                        </div>
                        <div className="customer-detail-item">
                          <strong>Hairline to Nape:</strong> {customerInfo?.hairlineToNape || 'N/A'}
                        </div>
                        <div className="customer-detail-item">
                          <strong>Ear to Ear:</strong> {customerInfo?.earToEar || 'N/A'}
                        </div>

                      </div>
                    )}

                  </div>

                </div>


              </div>

            </div>

            <div className="action-buttons action-buttons-mobile"
              style={{borderBottom:'1px solid var(--dark-border)'}}
            >
                  <button
                    className="btn btn-light rounded-pill shadow-sm mr-2"
                    onClick={() => { document.body.style.overflow = 'hidden'; setShowAttachCustomerModal(true); }}
                    title="Attach Different Customer"
                    style={{ alignSelf: 'flex-start', marginRight: 'auto' }}
                  >
                    <i className="fas fa-user-plus mr-1"></i>Attach Different Customer
                  </button>

              <button
                className="btn btn-light rounded-pill shadow-sm mr-2"
                onClick={handleGoBack}
              >
                <i className="fas fa-arrow-left mr-1"></i> Back
              </button>
              <button
                className="btn btn-light rounded-pill shadow-sm mr-2"
                onClick={() => setShowEditModal(true)}
              >
                <i className="fas fa-edit mr-1"></i> Edit Order
              </button>
              <button
                className="btn btn-primary rounded-pill shadow-sm mr-2"
                onClick={handlePrintWorksheet}
              >
                <i className="fas fa-print mr-1"></i> Print Worksheet
              </button>
              <button
                className="btn btn-success rounded-pill shadow-sm"
                onClick={handlePrintReceipt}
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #ffd700 100%)',
                  border: 'none',
                  color: '#2c2c2c',
                  fontWeight: '600'
                }}
              >
                <i className="fas fa-receipt mr-1"></i> Print Receipt
              </button>
            </div>
          </div>

          {/* Wig Details - Updated with responsive classes */}

            <div className="wig-details-header">
              <h6 className="card-title mb-0">
                <i className="fas fa-tshirt text-primary mr-2"></i>
                Wig Details
              </h6>
              {warrantyInfo.hasWarranty && warrantyInfo.isActive && (
                <div className="warranty-badge-header">
                  <i className="fas fa-shield-alt text-success mr-1"></i>
                  <small className="text-muted">
                    Under Warranty
                    {warrantyInfo.wigBarCode && (
                      <span className="warranty-barcode"> (Barcode: {warrantyInfo.wigBarCode})</span>
                    )}
                  </small>
                </div>
              )}
            </div>

            <div className="table-responsive-custom">
              <table className="table table-borderless">
                <thead>
                  <tr>
                    <th>Barcode</th>
                    <th>Cut Style</th>
                    <th>Wig Type</th>
                    <th>Net Size</th>
                    <th>Net Type</th>
                    <th>Base Color</th>
                    <th>Hair Length</th>
                    <th>Hair Type</th>
                    <th>Lace Type</th>
                    <th>Serial Number</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Barcode">
                      <EditableCell
                        value={orderDetails?.wigBarCode || ''}
                        onSave={handleSaveBarcode}
                        placeholder="N/A"
                        disabled={!['didip', 'noga'].includes(currentUser?.UserName?.toLowerCase())}
                      />
                    </td>
                    <td data-label="Cut Style">{wigDetails?.cutStyle || 'N/A'}</td>
                    <td data-label="Wig Type">{wigDetails?.wigType || 'N/A'}</td>
                    <td data-label="Net Size">{wigDetails?.netSize || 'N/A'}</td>
                    <td data-label="Net Type">{wigDetails?.netType || 'N/A'}</td>
                    <td data-label="Base Color">{wigDetails?.baseColor || 'N/A'}</td>
                    <td data-label="Hair Length">{wigDetails?.hairLength || 'N/A'}</td>
                    <td data-label="Hair Type">{wigDetails?.hairType || 'N/A'}</td>
                    <td data-label="Lace Type">{wigDetails?.laceType || 'N/A'}</td>
                    <td data-label="Serial Number">
                      <EditableCell
                        value={wigDetails?.serialCode || orderDetails?.wigSerialNo || ''}
                        onSave={handleSaveSerialCode}
                        placeholder="N/A"
                        disabled={!['didip', 'noga'].includes(currentUser?.UserName?.toLowerCase())}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Warranty Information Section - Lazy Loading Version */}
            {(() => {
              // Show load button if data hasn't been requested yet
              if (!warrantyInfo.loaded && !warrantyDataRequested) {
                return (


                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={loadWarrantyDataOnDemand}
                      >
                        <i className="fas fa-download mr-1"></i>
                        Load Warranty Info
                      </button>


                );
              }

              // Show loading spinner while fetching
              if (warrantyDataRequested && !warrantyInfo.loaded) {
                return (
                  <div className="warranty-section mt-4 p-3 bg-light rounded border-left border-info">
                    <div className="d-flex justify-content-center align-items-center py-3">
                      <div className="spinner-border spinner-border-sm mr-2" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <span>Loading warranty information...</span>
                    </div>
                  </div>
                );
              }

              // Show the full warranty section once loaded
              return warrantyInfo.loaded && (
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center flex-wrap">
                    <div className="warranty-info flex-grow-1">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-shield-alt text-primary mr-2"></i>
                        Warranty Information
                        {warrantyInfo.wigBarCode && (
                          <small className="text-muted ml-2">
                            (Barcode: {warrantyInfo.wigBarCode})
                          </small>
                        )}
                      </h6>

                      {warrantyInfo.hasWarranty ? (
                        <div className="warranty-details-enhanced">
                          {warrantyInfo.sourceOrderNo && warrantyInfo.sourceOrderNo !== orderDetails?.orderNo && (
                            <div className="warranty-source-info mb-3 p-2 bg-info text-white rounded">
                              <small>
                                <i className="fas fa-info-circle mr-1"></i>
                                Warranty created by order #{warrantyInfo.sourceOrderNo}
                              </small>
                            </div>
                          )}

                          <div className="warranty-grid">
                            <div className="warranty-item">
                              <div className="warranty-icon">
                                <i className="fas fa-play-circle text-success"></i>
                              </div>
                              <div className="warranty-content">
                                <div className="warranty-label">Start Date</div>
                                <div className="warranty-value">{formatDate(warrantyInfo.startDate)}</div>
                              </div>
                            </div>

                            <div className="warranty-item">
                              <div className="warranty-icon">
                                <i className="fas fa-stop-circle text-danger"></i>
                              </div>
                              <div className="warranty-content">
                                <div className="warranty-label">
                                  End Date

                                </div>

                                <div className="warranty-value">{formatDate(warrantyInfo.endDate)}</div>

                              </div>
                               {warrantyInfo.resetCount < 1 && (
                                    <button
                                      className="edit-date-btn"
                                      onClick={handleOpenWarrantyEdit}
                                      title="Edit warranty end date (requires balance paid in full)"
                                      style={{ fontSize: '0.8rem' }}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                  )}
                            </div>

                            <div className="warranty-item">
                              <div className="warranty-icon">
                                <i className="fas fa-heartbeat text-info"></i>
                              </div>
                              <div className="warranty-content">
                                <div className="warranty-label">Status</div>
                                <div className={`warranty-value ${getWarrantyStatusClass(warrantyInfo.status, warrantyInfo.daysRemaining)}`}>
                                  {getWarrantyStatusText(warrantyInfo.status, warrantyInfo.daysRemaining)}
                                </div>
                              </div>
                            </div>

                            <div className="warranty-item">
                              <div className="warranty-icon">
                                <i className="fas fa-redo-alt text-warning"></i>
                              </div>
                              <div className="warranty-content">
                                <div className="warranty-label">Resets Available</div>
                                <div className="warranty-value">
                                  {1 - (warrantyInfo.resetCount || 0)}/1
                                  {(warrantyInfo.resetCount || 0) >= 1 && (
                                    <small className="text-muted d-block">(None remaining)</small>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>



                          {warrantyInfo.daysRemaining !== null && (() => {
                            // Calculate total warranty days based on reset count
                            // 1-year warranty = 365 days, 9-month warranty (after reset) = 270 days
                            const totalWarrantyDays = warrantyInfo.resetCount >= 1 ? 270 : 365;
                            const daysElapsed = totalWarrantyDays - warrantyInfo.daysRemaining;
                            const progressPercent = Math.max(0, Math.min(100, (daysElapsed / totalWarrantyDays) * 100));

                            return (
                              <div className="warranty-progress-enhanced mt-4">
                                <div className="progress-header mb-2">
                                  <span className="progress-label">Warranty Coverage Progress</span>
                                  <span className="progress-percentage">
                                    {Math.round(progressPercent)}% Complete
                                  </span>
                                </div>

                                <div
                                  style={{
                                    width: '100%',
                                    height: '12px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${progressPercent}%`,
                                      height: '100%',
                                      backgroundColor: warrantyInfo.status === 'Expired' ? '#6c757d' :
                                                      warrantyInfo.daysRemaining <= 30 ? '#dc3545' :
                                                      warrantyInfo.daysRemaining <= 90 ? '#ffc107' :
                                                      '#28a745',
                                      transition: 'width 0.6s ease',
                                      borderRadius: '5px'
                                    }}
                                    role="progressbar"
                                    aria-valuenow={Math.round(progressPercent)}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  ></div>
                                </div>

                                <div className="progress-footer mt-1">
                                  <small className="text-muted">
                                    {warrantyInfo.daysRemaining} days remaining
                                  </small>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Manual Edit Reason Display */}
                          {warrantyInfo.resetCount >= 1 && warrantyInfo.status && warrantyInfo.status !== 'Active' && warrantyInfo.status !== 'Expired' && (
                            <div className="alert alert-info mt-3 mb-3" style={{
                              backgroundColor: '#e7ffe7ff',
                              borderLeft: '4px solid #21f32cff',
                              padding: '8px 12px'
                            }}>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-info-circle mr-2" style={{ color: '#6ef321ff', fontSize: '0.9rem' }}></i>
                                <span style={{ color: '#424242', fontSize: '0.85rem' }}>
                                  <strong style={{ color: '#38d219ff' }}>Manual Adjustment:</strong> {warrantyInfo.status}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-warranty-enhanced">
                          <div className="no-warranty-content">
                            <h6 className="mb-1">No Warranty Coverage</h6>
                            <p className="text-muted mb-0">
                              {warrantyInfo.message || 'No warranty found for this barcode'}
                            </p>
                            {warrantyInfo.wigBarCode && (
                              <small className="text-muted d-block mt-1">
                                Checked barcode: {warrantyInfo.wigBarCode}
                              </small>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Reset Warranty Button */}
                    {warrantyInfo.hasWarranty && (
                      <div className="warranty-actions mt-3 mt-md-0 ml-md-3">
                        <button
                          className={`btn ${canResetWarranty() ? 'btn-warning' : 'btn-secondary'} btn-sm rounded-pill`}
                          onClick={handleResetWarranty}
                          disabled={!canResetWarranty()}
                          title={
                            !canResetWarranty() ? (
                              normalizeOrderType(orderDetails?.orderType) !== 'New Wig' ? 'Only New Wig orders can reset warranty' :
                              warrantyInfo.resetCount >= 1 ? 'Warranty already reset once' :
                              'Balance must be paid in full to reset warranty'
                            ) : 'Reset warranty for 9 additional months'
                          }
                        >
                          <i className="fas fa-redo mr-1"></i>
                          Reset Warranty
                        </button>

                        {!canResetWarranty() && (
                          <small className="text-muted d-block mt-1 text-center">
                            {normalizeOrderType(orderDetails?.orderType) !== 'New Wig' ?
                              'New Wig orders only' :
                              warrantyInfo.resetCount >= 1 ?
                                'Max resets reached' :
                                'Pay balance to enable'
                            }
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>
      </div>



    {/* Production Schedule Tab */}
    <div className="tab-content d-block">
      {productionScheduleReady ? (
        <ProductionSchedule
          key={productionScheduleKey}
          orderNo={orderDetails?.orderNo}
          onClientRejectOrder={handleClientRejectOrder}
          onOpenTransferModal={openTransferModal}
          currentUser={currentUser}
          ordercustomerName={customerInfo?.customerName}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
    </div>




    {/* Media Attachments Section */}
    <AttachMediaModal
      isOpen={showMediaModal}
      onClose={() => setShowMediaModal(false)}
      orderNo={orderDetails?.orderNo}
      roomTasks={roomTasks}
    />
    {/* <div className="card mb-4 shadow-sm border-0" style={{ overflow: 'hidden', background: 'var(--dark-surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-photo-video" style={{ color: 'var(--text-primary)', fontSize: '16px' }}></i>
              <span style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '20px' }}>Media</span>
            </div>
            <button
              className="btn btn-success rounded-pill shadow-sm"
              onClick={() => setShowMediaModal(true)}
            >
              <i className="fas fa-photo-video mr-1"></i> View / Attach Media
            </button>
          </div>
        </div> */}

    {/* Invoice Tab - Mobile responsive */}
    <div className="tab-content d-block">
      <div className="card shadow-sm">
        <div className="card-title">

        {/* Invoice Section */}
        <div className="d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start mb-3">
          <h6 className="card-title mb-md-0 mb-1">
            <i className="fas fa-file-invoice-dollar text-primary mr-2"></i>
            Invoice #{paymentSummary?.invoiceNumber || 'Not generated'}
          </h6>
          <div className="d-flex gap-2">
            <button
              className="btn btn-success rounded-pill"
              onClick={() => setShowPaymentModal(true)}
              disabled={!orderDetails?.orderNo}
              title="Add a new payment to this order"
            >
              <i className="fas fa-plus mr-1"></i>
              Add Payment
            </button>
            <button
              className="btn btn-success rounded-pill shadow-sm"
              onClick={handlePrintReceipt}
              style={{
                background: 'linear-gradient(135deg, #d4af37 0%, #ffd700 100%)',
                border: 'none',
                color: '#2c2c2c',
                fontWeight: '600'
              }}
            >
              <i className="fas fa-receipt mr-1"></i> Print Receipt
            </button>
          </div>
        </div>

          <hr />

          {renderInvoiceCalculation()}

        </div>
      </div>
    </div>

    {showPaymentModal && (
      <PaymentModal
        orderNo={orderDetails?.orderNo}
        customerId={orderDetails?.OrderCustomerNo || orderDetails?.customerNo}
        currentBalance={(() => {
          const orderTotal = lineItemsData.items.reduce((sum, item) => {
            return sum + parseFloat(item.OrderItemExtendedCost || 0);
          }, 0);
          const totalPaid = paymentsData.payments.reduce((sum, payment) => {
            return sum + parseFloat(payment.PaymentAmount || 0);
          }, 0);
          return orderTotal - totalPaid;
        })()}
        taxInfo={calculateTaxInfoForPayment()} // 🆕 NEW: Pass tax info
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
      />
    )}

    {showAutoCompletePrompt && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10001
      }}>
        <div style={{
          background: '#1e1e2e', border: '1px solid #444', borderRadius: '12px',
          padding: '28px', width: '400px', maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: '16px'
        }}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '16px' }}>
            ✅ Order Fully Paid
          </h3>
          <p style={{ margin: 0, color: '#aaa', fontSize: '13px', lineHeight: '1.5' }}>
            The balance on order <strong style={{ color: '#fff' }}>#{orderDetails?.orderNo}</strong> is now paid in full.
            Would you like to auto-complete it?
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowAutoCompletePrompt(false)}
              disabled={isAutoCompleting}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: '1px solid #555',
                background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: '13px'
              }}
            >
              Not Yet
            </button>
            <button
              onClick={handleAutoCompleteFromPrompt}
              disabled={isAutoCompleting}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none',
                background: '#10b981', color: '#fff', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600'
              }}
            >
              {isAutoCompleting ? 'Completing…' : 'Complete Order'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showReceipt && (
      <OrderReceipt
        orderDetails={orderDetails}
        customerInfo={customerInfo}
        wigDetails={wigDetails}
        orderItems={orderItems || []}
        payments={payments || []}
        totals={{
          subtotal: paymentSummary?.subtotal || 0,
          tax: paymentSummary?.tax || 0,
          shipping: paymentSummary?.shipping || 0,
          deposit: paymentSummary?.deposit || 0,
          discount: paymentSummary?.discount || 0,
          taxRate: paymentSummary?.taxRate || 8.375
        }}
        paymentSummary={paymentSummary || {}}
        isVisible={showReceipt}
        onClose={handleCloseReceipt}
        // 🆕 NEW: Pass the scraped data from your existing state
        lineItemsData={lineItemsData}
        paymentsData={paymentsData}
      />
    )}

    {showWorksheet && (
      <OrderWorksheet
        orderDetails={worksheetSpecificData || orderDetails} // Use worksheet data if available
        customerInfo={customerInfo}
        wigDetails={worksheetSpecificData || wigDetails}     // Include wig weight here
        roomTasks={worksheetSpecificData?.roomTasks || roomTasks}
        isVisible={showWorksheet}
        onClose={handleCloseWorksheet}
      />
    )}

{showRejectionModal && (
  <ClientRejectionModal
    isOpen={showRejectionModal}
    onClose={() => setShowRejectionModal(false)}
    orderData={orderData}
    onRejectionComplete={handleRejectionComplete}
    customerAppointments={customerAppointments}
    currentUser={currentUser}
  />
)}

<OrderEditModal
  isOpen={showEditModal}
  onClose={() => setShowEditModal(false)}
  orderData={orderData}
  onEditComplete={handleEditComplete}
  currentUser={currentUser}
/>

<WigTransferModal />
<AttachCustomerModal
  isOpen={showAttachCustomerModal}
  onClose={() => setShowAttachCustomerModal(false)}
  orderNo={orderDetails?.orderNo}
  onSuccess={() => fetchOrderData(orderDetails?.orderNo)}
/>
<CreateAppointmentModal
  isOpen={showModal}
  onClose={() => setShowModal(false)}
  customerInfo={{
    customerNo: customerInfo?.ID,
    ID: customerInfo?.ID,
    customerName: customerInfo?.customerName,
    phone: customerInfo?.phone,
    email: customerInfo?.email,
    address: customerInfo?.address,
    city: customerInfo?.city,
    state: customerInfo?.state,
    zip: customerInfo?.zip
  }}
  currentUser={currentUser?.UserName || currentUser}
  prefillData={{
    Title: 'Customer Pickup',           // This should prefill the appointment type
    EventDesc: 'Customer Due Date'      // This should prefill the event description
  }}
  onAppointmentCreated={async (newAppointment) => {
    if (customerInfo?.ID) {
      try {
        console.log('Refreshing appointments after creation for customer:', customerInfo.ID);
        await handleFetchCustomerAppointments(customerInfo.ID);
      } catch (error) {
        console.error('Error refreshing appointments:', error);
      }
    }
  }}
/>

      {/* Warranty End Date Edit Modal */}
      {editingWarrantyEndDate && (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="modal-header mb-3">
          <h5 className="modal-title">
            <i className="fas fa-edit mr-2"></i>
            Edit Warranty End Date
          </h5>
          <button
            className="close"
            onClick={() => {
              setEditingWarrantyEndDate(false);
              setWarrantyEditForm({ newEndDate: '', reason: '' });
            }}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="alert alert-warning mb-3">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <strong>Important:</strong> This is a manual override. This action can only be performed once and will increment the warranty reset count.
          </div>

          <div className="form-group mb-3">
            <label htmlFor="warranty-current-end-date" className="form-label">
              <strong>Current End Date:</strong>
            </label>
            <div className="form-control-plaintext">
              {formatDate(warrantyInfo.endDate)}
            </div>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="warranty-new-end-date" className="form-label">
              <strong>New End Date:</strong> <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              id="warranty-new-end-date"
              className="form-control"
              value={warrantyEditForm.newEndDate}
              onChange={(e) => setWarrantyEditForm({ ...warrantyEditForm, newEndDate: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group mb-3">
            <label htmlFor="warranty-edit-reason" className="form-label">
              <strong>Reason for Manual Update:</strong> <span className="text-danger">*</span>
            </label>
            <textarea
              id="warranty-edit-reason"
              className="form-control"
              rows="4"
              placeholder="Explain why you are updating the warranty manually instead of using the proper channels (minimum 10 characters)..."
              value={warrantyEditForm.reason}
              onChange={(e) => setWarrantyEditForm({ ...warrantyEditForm, reason: e.target.value })}
              maxLength="500"
            />
            <small className="form-text text-muted">
              {warrantyEditForm.reason.length}/500 characters (minimum 10 required)
            </small>
          </div>
        </div>

        <div className="modal-footer d-flex justify-content-end">
          <button
            className="btn btn-secondary mr-2"
            onClick={() => {
              setEditingWarrantyEndDate(false);
              setWarrantyEditForm({ newEndDate: '', reason: '' });
            }}
            disabled={savingWarrantyEdit}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveWarrantyEdit}
            disabled={savingWarrantyEdit || !warrantyEditForm.newEndDate || warrantyEditForm.reason.trim().length < 10}
          >
            {savingWarrantyEdit ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )}
  </div>
  );
};
export default WorkOrderProfile;